<!--
  AmoeUserForm: AMOE 11 字段表单编排组件
  - 消费 Plan 04 composable 引用(fields / sms / lottery / availability)+ Plan 05a 3 个原子
  - 纯 UI 编排,不 import apiClient(D-39);只 type-only import composable 接口
  - D-05 字段顺序严格对齐(RH-W3):
      1=First+Last / 2=Age / 3=Country+Phone / 4=Address1 / 5=Address2(可选,D-22) /
      6=City / 7=State+Zip / 8=Email / 9=SMS / 10=Agreement / 11=Submit
  - 双字段同行:#1 firstName+lastName、#3 countryCode+phoneNumber、#7 state+zipCode
  - RH-B4:SMS Code suffix-disabled 绑 sms.canSend(canSend 已合并 smsSending)
  - D-15:PC + H5 单组件响应式(page-${pageType});Figma token 对齐(D-16)
-->
<template>
  <div class="amoe-form" :class="{ isPC: pageType === 'PC' }">
    <!-- D-05 #1: First + Last Name 同行 -->
    <div class="row row-double">
      <FormItem
        :value="fields.values.firstName.value"
        :label="t('amoe.form.firstName.label')"
        :placeholder="t('amoe.form.firstName.placeholder')"
        :required="true"
        :error="fields.errors.value.firstName"
        type="text"
        :page-type="pageType"
        h5-gutter="form"
        content-gap="modal"
        width="half"
        @update:value="fields.setValue('firstName', $event)"
        @blur="fields.validate('firstName')"
      />
      <FormItem
        :value="fields.values.lastName.value"
        :label="t('amoe.form.lastName.label')"
        :placeholder="t('amoe.form.lastName.placeholder')"
        :required="true"
        :error="fields.errors.value.lastName"
        type="text"
        :page-type="pageType"
        h5-gutter="form"
        content-gap="modal"
        width="half"
        @update:value="fields.setValue('lastName', $event)"
        @blur="fields.validate('lastName')"
      />
    </div>

    <!-- D-05 #2: Age -->
    <FormItem
      :value="fields.values.age.value"
      :label="t('amoe.form.age.label')"
      :placeholder="t('amoe.form.age.placeholder')"
      :required="true"
      :error="fields.errors.value.age"
      type="text"
      :page-type="pageType"
      h5-gutter="form"
      content-gap="modal"
      @update:value="fields.setValue('age', $event)"
      @blur="fields.validate('age')"
    />

    <!-- D-05 #3: Phone Number(Figma 中 country code 合并在同一个输入框内) -->
    <div
      class="phone-field"
      :class="{ 'has-error': !!fields.errors.value.phoneNumber }"
    >
      <div class="phone-field-box">
        <div class="label-row">
          <label class="label">{{ t('amoe.form.phoneNumber.label') }}</label>
          <span class="req" aria-hidden="true">*</span>
        </div>
        <div class="phone-input-row">
          <button
            class="country-code-trigger"
            type="button"
            aria-label="Select country code"
            @click.stop="openCountryPicker"
          >
            <span class="value">{{ fields.values.countryCode.value }}</span>
            <span class="chevron" aria-hidden="true" />
          </button>
          
          <OptionPicker
            v-if="pageType === 'PC'"
            :visible="countryPickerOpen"
            :page-type="pageType"
            title=""
            :search-placeholder="t('amoe.form.phoneNumber.label')"
            :options="countryOptions"
            :value="fields.values.countryCode.value"
            @update:value="onCountryCodeSelected"
            @close="countryPickerOpen = false"
          >
            <template #option="{ option }">
              <div class="country-option">
                <span class="country-name">{{ option.name || option.label }}</span>
                <span v-if="option.code" class="country-code">{{ option.code }}</span>
              </div>
            </template>
          </OptionPicker>

          <input
            :value="fields.values.phoneNumber.value"
            :placeholder="t('amoe.form.phoneNumber.placeholder')"
            type="text"
            @input="onPhoneInput"
            @blur="fields.validate('phoneNumber')"
          />
        </div>
      </div>
      <div
        class="error-msg"
        :class="{ 'is-empty': !fields.errors.value.phoneNumber }"
        aria-live="polite"
      >
        {{ fields.errors.value.phoneNumber }}
      </div>
    </div>

    <!-- D-05 #4: Address 1 -->
    <FormItem
      :value="fields.values.addressLine1.value"
      :label="t('amoe.form.addressLine1.label')"
      :placeholder="t('amoe.form.addressLine1.placeholder')"
      :required="true"
      :error="fields.errors.value.addressLine1"
      type="text"
      :page-type="pageType"
      h5-gutter="form"
      content-gap="modal"
      @update:value="fields.setValue('addressLine1', $event)"
      @blur="fields.validate('addressLine1')"
    />

    <!-- D-05 #5: Address 2(optional D-22) -->
    <FormItem
      :value="fields.values.addressLine2.value"
      :label="t('amoe.form.addressLine2.label')"
      :placeholder="t('amoe.form.addressLine2.placeholder')"
      :required="false"
      :error="fields.errors.value.addressLine2"
      type="text"
      :page-type="pageType"
      h5-gutter="form"
      content-gap="modal"
      @update:value="fields.setValue('addressLine2', $event)"
      @blur="fields.validate('addressLine2')"
    />

    <!-- D-05 #6: City -->
    <FormItem
      :value="fields.values.city.value"
      :label="t('amoe.form.city.label')"
      :placeholder="t('amoe.form.city.placeholder')"
      :required="true"
      :error="fields.errors.value.city"
      type="text"
      :page-type="pageType"
      h5-gutter="form"
      content-gap="modal"
      @update:value="fields.setValue('city', $event)"
      @blur="fields.validate('city')"
    />

    <!-- D-05 #7: H5 State + Zip 同行；PC 按视觉稿拆成两个单行字段 -->
    <div
      class="row state-zip-row"
      :class="{ 'row-double': pageType !== 'PC' }"
    >
      <div
        class="picker-trigger state-trigger"
        role="button"
        tabindex="0"
        @click.stop="openStatePicker"
        @keydown.enter.prevent="openStatePicker"
        @keydown.space.prevent="openStatePicker"
      >
        <div
          class="field-box"
          :class="{ 'has-error': !!fields.errors.value.state }"
        >
          <div class="state-text-column">
            <div class="label-row">
              <label class="label">{{ t('amoe.form.state.label') }}</label>
              <span class="req" aria-hidden="true">*</span>
            </div>
            <span
              class="value"
              :class="{ 'is-placeholder': !fields.values.state.value }"
              >{{ selectedStateLabel }}</span
            >
          </div>
          <span class="chevron" aria-hidden="true" />
          
          <OptionPicker
            v-if="pageType === 'PC'"
            :visible="statePickerOpen"
            :page-type="pageType"
            title=""
            :search-placeholder="t('amoe.form.state.label')"
            :options="stateOptions"
            :value="fields.values.state.value"
            @update:value="onStateSelected"
            @close="statePickerOpen = false"
          />
        </div>
        <div
          class="error-msg"
          :class="{ 'is-empty': !fields.errors.value.state }"
          aria-live="polite"
        >
          {{ fields.errors.value.state }}
        </div>
      </div>
      <FormItem
        :value="fields.values.zipCode.value"
        :label="t('amoe.form.zipCode.label')"
        :placeholder="t('amoe.form.zipCode.placeholder')"
        :required="true"
        :error="fields.errors.value.zipCode"
        type="text"
        :page-type="pageType"
        h5-gutter="form"
        content-gap="modal"
        :width="pageType === 'PC' ? 'full' : 'half'"
        @update:value="fields.setValue('zipCode', $event)"
        @blur="fields.validate('zipCode')"
      />
    </div>

    <!-- D-05 #8: Email -->
    <FormItem
      :value="fields.values.email.value"
      :label="t('amoe.form.email.label')"
      :placeholder="t('amoe.form.email.placeholder')"
      :required="true"
      :error="fields.errors.value.email"
      type="text"
      :page-type="pageType"
      h5-gutter="form"
      content-gap="modal"
      @update:value="fields.setValue('email', $event)"
      @blur="fields.validate('email')"
    />

    <!-- D-05 #9: SMS Code(suffix Send 按钮;RH-B4:suffix-disabled=!sms.canSend.value) -->
    <FormItem
      class="sms-code-field"
      :value="fields.values.smsCode.value"
      :placeholder="t('amoe.form.smsCode.label')"
      :required="false"
      :error="fields.errors.value.smsCode"
      type="text"
      :page-type="pageType"
      :suffix-text="sms.btnLabel.value"
      :suffix-disabled="!sms.canSend.value"
      h5-gutter="form"
      content-gap="modal"
      @update:value="fields.setValue('smsCode', $event)"
      @suffix-click="$emit('send-sms')"
      @blur="fields.validate('smsCode')"
    />

    <!-- D-05 #10: Agreement checkbox -->
    <AgreementCheckbox
      :checked="agreementChecked"
      :page-type="pageType"
      @update:checked="$emit('update:agreement-checked', $event)"
      @agreement:tos-click="$emit('agreement:tos-click')"
      @agreement:privacy-click="$emit('agreement:privacy-click')"
    />

    <!-- D-05 #11: Submit CTA -->
    <SubmitButton
      :label="t('amoe.form.submit.label')"
      :disabled="submitDisabled"
      :loading="submitLoading"
      :page-type="pageType"
      @click="$emit('submit')"
    />

    <!-- Pickers(由 #3 / #7 触发,独立于 D-05 编号) -->
    <!-- PC 端 OptionPicker 已移动到对应字段的 field-box 内部以支持 position: absolute 下拉效果 -->
    <template v-if="pageType !== 'PC'">
      <OptionPicker
        :visible="countryPickerOpen"
        :page-type="pageType"
        :title="t('amoe.form.phoneNumber.label')"
        :search-placeholder="t('amoe.form.phoneNumber.label')"
        :options="countryOptions"
        :value="fields.values.countryCode.value"
        @update:value="onCountryCodeSelected"
        @close="countryPickerOpen = false"
      >
        <template #option="{ option }">
          <div class="country-option">
            <span class="country-name">{{ option.name || option.label }}</span>
            <span v-if="option.code" class="country-code">{{ option.code }}</span>
          </div>
        </template>
      </OptionPicker>
      <OptionPicker
        :visible="statePickerOpen"
        :page-type="pageType"
        :title="t('amoe.form.state.label')"
        :search-placeholder="t('amoe.form.state.label')"
        :options="stateOptions"
        :value="fields.values.state.value"
        @update:value="onStateSelected"
        @close="statePickerOpen = false"
      />
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue-demi';
import type { PropType } from 'vue-demi';
import { FormItem } from '@ark/mets-frontend-shared';
import { COUNTRIES, US_STATES, US_STATES_BY_CODE } from '@ark/mets-shared';
import SubmitButton from './SubmitButton.vue';
import AgreementCheckbox from './AgreementCheckbox.vue';
import OptionPicker from './OptionPicker.vue';
import type { PickerOption } from './types';
import type {
  UseFormFieldsReturn,
  UseSmsCountdownReturn,
  UseLotteryReturn,
  UseAvailabilityReturn,
} from '../composables';
import t from '../render/utils/i18n';

export default defineComponent({
  name: 'AmoeUserForm',
  components: { FormItem, SubmitButton, AgreementCheckbox, OptionPicker },
  props: {
    pageType: { type: String as () => 'H5' | 'PC', required: true },
    seasonSign: { type: String, required: true },
    agreementChecked: { type: Boolean, default: false },
    fields: {
      type: Object as PropType<UseFormFieldsReturn>,
      required: true,
    },
    sms: {
      type: Object as PropType<UseSmsCountdownReturn>,
      required: true,
    },
    lottery: {
      type: Object as PropType<UseLotteryReturn>,
      required: true,
    },
    availability: {
      type: Object as PropType<UseAvailabilityReturn>,
      required: true,
    },
  },
  emits: [
    'submit',
    'send-sms',
    'update:agreement-checked',
    'agreement:tos-click',
    'agreement:privacy-click',
  ],
  setup(props) {
    console.log('[DEBUG] AmoeUserForm props.pageType init:', props.pageType);

    const countryPickerOpen = ref(false);
    const statePickerOpen = ref(false);

    const countryOptions = computed<readonly PickerOption[]>(() => {
      // 保持原始顺序，因为热门国家（如中国、新加坡）通常放在最前面
      const originalCountries = [...COUNTRIES];
      return originalCountries.map((c) => ({
        value: c.phoneCode,
        label: `${c.name} (${c.phoneCode})`,
        name: c.name,
        code: `(${c.phoneCode})`,
        group: c.name.charAt(0).toUpperCase(),
      }));
    });
    const stateOptions = computed<readonly PickerOption[]>(() => {
      const sortedStates = [...US_STATES].sort((a, b) =>
        a.name.localeCompare(b.name),
      );
      return sortedStates.map((s) => ({
        value: s.code,
        label: s.name,
        group: s.name.charAt(0).toUpperCase(),
      }));
    });

    const selectedStateLabel = computed<string>(() => {
      const code = props.fields.values.state.value;
      if (!code) return '';
      const entry = US_STATES_BY_CODE[code];
      return entry?.name ?? code;
    });

    function openCountryPicker(): void {
      countryPickerOpen.value = true;
    }
    function openStatePicker(): void {
      statePickerOpen.value = true;
    }
    function onCountryCodeSelected(v: string): void {
      props.fields.setValue('countryCode', v);
    }
    function onPhoneInput(ev: Event): void {
      props.fields.setValue(
        'phoneNumber',
        (ev.target as HTMLInputElement).value,
      );
    }
    function onStateSelected(v: string): void {
      props.fields.setValue('state', v);
      props.fields.validate('state');
    }

    const submitDisabled = computed<boolean>(() => {
      return (
        !props.availability.hasActiveRound.value ||
        !props.agreementChecked ||
        !props.fields.canSubmit.value ||
        props.lottery.state.value.kind !== 'idle' ||
        props.seasonSign.length === 0
      );
    });

    const submitLoading = computed<boolean>(
      () => props.lottery.state.value.kind === 'submitting',
    );

    return {
      countryPickerOpen,
      statePickerOpen,
      countryOptions,
      stateOptions,
      selectedStateLabel,
      openCountryPicker,
      openStatePicker,
      onCountryCodeSelected,
      onPhoneInput,
      onStateSelected,
      submitDisabled,
      submitLoading,
      t,
    };
  },
});
</script>

<style scoped>
.amoe-form {
  display: flex;
  flex-direction: column;
  font-family: MiSans, Arial, sans-serif;
  box-sizing: border-box;
}

.row-double {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
}

.picker-trigger {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-width: 0;
  cursor: pointer;
  box-sizing: border-box;
}

.picker-trigger:focus-visible {
  outline: 2px solid #2b78f5;
  outline-offset: 2px;
}

.picker-trigger .label-row {
  display: flex;
  align-items: center;
}

.picker-trigger .label {
  color: #626262;
}

.picker-trigger .req {
  color: #e53751;
  margin-left: 2px;
}

.picker-trigger .field-box {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  border: 2px solid #e3e4e5;
  background: #ffffff;
  color: #0f1112;
}

.picker-trigger .field-box.has-error {
  border-color: #e53751;
}

.picker-trigger .value.is-placeholder {
  color: #bbbdc0;
}

.state-text-column {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
}

.state-text-column .value:empty::before {
  content: '\00a0';
}

.state-text-column .value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.picker-trigger .chevron,
.phone-field .chevron {
  display: inline-block;
  flex-shrink: 0;
  box-sizing: border-box;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  color: #606266;
  transform: rotate(45deg);
  position: relative;
  top: -2px;
}

.picker-trigger .error-msg {
  color: #e53751;
}

.phone-field {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.phone-field-box {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  border: 2px solid #e3e4e5;
  background: #ffffff;
}

.phone-field.has-error .phone-field-box {
  border-color: #e53751;
}

.phone-field .label-row,
.phone-input-row {
  display: flex;
  align-items: center;
}

.phone-field .label {
  color: #626262;
}

.phone-field .req {
  color: #e53751;
}

.country-code-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  padding: 0;
  border: none;
  background: transparent;
  color: #0f1112;
  font: inherit;
  cursor: pointer;
}

.phone-input-row input {
  min-width: 0;
  flex: 1 1 auto;
  border: none;
  outline: none;
  background: transparent;
  color: #121212;
  font: inherit;
}

.phone-input-row input::placeholder {
  color: transparent;
}

.phone-field .error-msg {
  box-sizing: border-box;
  overflow: hidden;
  color: #e53751;
}

.phone-field .error-msg.is-empty {
  color: transparent;
}

/* H5: 设计稿 750w, 100px = 1rem; Figma 实测 form card top 80px / 字段间 spacer 45px (默认 mobile-first) */
.amoe-form {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 0.8rem 0 1rem;
  background: #ffffff;
  border-radius: 0.32rem 0.32rem 0 0;
  overflow-x: hidden;
}

.amoe-form > * + * {
  margin-top: 0;
}

.amoe-form .row-double {
  padding: 0 0.34rem;
}

.amoe-form .row-double > .picker-trigger {
  flex: 0 0 3.31rem;
  width: 3.31rem;
  min-width: 0;
}

.amoe-form .row-double > .form-item.h5-gutter-form {
  flex: 0 0 3.31rem;
  width: 3.31rem;
  min-width: 0;
  padding-right: 0;
  padding-left: 0;
}

.amoe-form .row-double > .form-item.h5-gutter-form + .form-item.h5-gutter-form,
.amoe-form .row-double > .picker-trigger + .form-item.h5-gutter-form {
  margin-left: 0.2rem;
}

.amoe-form ::v-deep .form-item.h5-gutter-form {
  padding-right: 0.34rem;
  padding-left: 0.34rem;
}

.amoe-form ::v-deep .field-box {
  gap: 0.04rem;
  border-width: 0.0192rem;
  border-radius: 0.1538rem;
  padding: 0.21rem 0.24rem;
  min-height: 1.32rem;
}

.amoe-form ::v-deep .content-gap-modal .label-row + .input-row {
  margin-top: 0.04rem;
}

.amoe-form ::v-deep .label,
.amoe-form ::v-deep .required-mark {
  font-size: 0.26rem;
  line-height: 1.4em;
  font-weight: 330;
}

.amoe-form ::v-deep .input-row input,
.amoe-form ::v-deep .suffix-button {
  font-size: 0.3rem;
  line-height: 1.4em;
}

.amoe-form ::v-deep .input-row input::placeholder {
  color: transparent;
}

.amoe-form .state-text-column {
  gap: 0.24rem;
}

.amoe-form .picker-trigger .label-row {
  padding: 0 0.04rem;
}

.amoe-form .picker-trigger .label,
.amoe-form .picker-trigger .req {
  font-size: 0.26rem;
  line-height: 1.4em;
  font-weight: 330;
}

.amoe-form .picker-trigger .field-box {
  flex-direction: row;
  align-items: center;
  padding: 0.21rem 0.24rem;
  border-width: 0.0192rem;
  border-radius: 0.1538rem;
  min-height: 1.32rem;
  gap: 0.16rem;
}

.amoe-form .picker-trigger .label-row + .value {
  display: block;
  margin-top: 0.04rem;
}

.amoe-form .phone-field {
  padding: 0 0.34rem;
}

.amoe-form .phone-field-box {
  border-width: 0.0192rem;
  border-radius: 0.1538rem;
  padding: 0.21rem 0.24rem;
}

.amoe-form .phone-field .label-row + .phone-input-row {
  margin-top: 0.04rem;
}

.amoe-form .phone-field .label,
.amoe-form .phone-field .req {
  font-size: 0.26rem;
  line-height: 1.4em;
  font-weight: 330;
}

.amoe-form .phone-input-row {
  font-size: 0.3rem;
  line-height: 1.4em;
}

.amoe-form .phone-input-row input {
  margin-left: 0.3rem;
}

.amoe-form .state-text-column .value {
  font-size: 0.3rem;
  line-height: 1.4em;
}

.amoe-form .picker-trigger .chevron,
.amoe-form .phone-field .chevron {
  width: 0.16rem;
  height: 0.16rem;
  margin-left: 0.2rem;
  color: #606266;
  top: -0.03rem;
}

.amoe-form .picker-trigger .error-msg {
  box-sizing: border-box;
  overflow: hidden;
  height: 0.45rem;
  font-size: 0.26rem;
  padding: 0.05rem 0.23rem 0;
  line-height: 1.4em;
}

.amoe-form .picker-trigger .error-msg.is-empty {
  color: transparent;
}

.amoe-form .phone-field .error-msg {
  box-sizing: border-box;
  overflow: hidden;
  height: 0.45rem;
  font-size: 0.26rem;
  padding: 0.05rem 0.23rem 0;
  line-height: 1.4em;
}

.amoe-form ::v-deep .sms-code-field .field-box {
  justify-content: center;
  min-height: 1.32rem;
}

.amoe-form ::v-deep .sms-code-field .input-row input::placeholder {
  color: #606266;
  opacity: 1;
}

.amoe-form ::v-deep .agreement {
  padding: 0 0.34rem;
}

.amoe-form ::v-deep .submit-btn {
  align-self: center;
  margin-top: 0.4rem;
}

/* PC: 设计稿 752w; 大写 PX 用于跳过 postcss-pxtorem，避免固定 PC 尺寸被转成 rem 后受根字号影响 */
.amoe-form.isPC {
  width: 752PX;
  max-width: 100%;
  margin: 0 auto;
  padding: 40PX 30PX;
  border-radius: 20PX;
  /* 强制重置可能从父级继承的 flex 行为，确保 margin: 0 auto 生效 */
  flex: none;
  align-self: center;
  /* 防止被外部 flex 容器拉伸 */
  flex-shrink: 0;
  overflow: visible; /* 允许下拉框溢出 */
}

.amoe-form.isPC > * + * {
  margin-top: 8PX;
}

.amoe-form.isPC .row-double {
  padding: 0;
}

.amoe-form.isPC .row-double > * + * {
  margin-left: 20PX;
}

.amoe-form.isPC .row-double > .picker-trigger,
.amoe-form.isPC .row-double > .form-item.h5-gutter-form {
  width: calc((100% - 20PX) / 2);
  flex: none;
}

.amoe-form.isPC .state-zip-row > * + * {
  margin-top: 8PX;
  margin-left: 0;
}

/* Override H5 ::v-deep styles for FormItem */
.amoe-form.isPC ::v-deep .form-item.h5-gutter-form {
  padding-right: 0;
  padding-left: 0;
}

.amoe-form.isPC ::v-deep .field-box {
  gap: 2.666667PX;
  border-width: 1.333333PX;
  border-radius: 10PX;
  padding: 13PX 16PX;
  min-height: 0;
}

.amoe-form.isPC ::v-deep .content-gap-modal .label-row + .input-row {
  margin-top: 2.666667PX;
}

.amoe-form.isPC ::v-deep .label,
.amoe-form.isPC ::v-deep .required-mark {
  font-size: 18PX;
  line-height: 1.4em;
  font-weight: 330;
}

.amoe-form.isPC ::v-deep .input-row input,
.amoe-form.isPC ::v-deep .suffix-button {
  font-size: 18PX;
  line-height: 1.4em;
}

.amoe-form.isPC ::v-deep .form-item.isPC .error-message {
  position: static;
  padding: 4PX 16PX 0;
  height: 24PX;
  font-size: 14PX;
  line-height: 1.4em;
}

/* picker-trigger overrides */
.amoe-form.isPC .picker-trigger .label,
.amoe-form.isPC .picker-trigger .req {
  font-size: 18PX;
  line-height: 1.4em;
  font-weight: 330;
}

.amoe-form.isPC .picker-trigger .field-box {
  border-width: 1.333333PX;
  border-radius: 10PX;
  padding: 13PX 16PX;
  min-height: 0;
  height: auto;
  gap: 2.666667PX;
  margin-top: 0;
  position: relative;
}

.amoe-form.isPC .state-text-column {
  gap: 10PX;
}

.amoe-form.isPC .picker-trigger .label-row + .value {
  margin-top: 2.666667PX;
}

.amoe-form.isPC .picker-trigger .error-msg {
  font-size: 14PX;
  padding: 4PX 16PX 0;
  line-height: 1.4em;
  height: 24PX;
}

/* phone-field overrides */
.amoe-form.isPC .phone-field {
  padding: 0;
}

.amoe-form.isPC .phone-field-box {
  border-width: 1.333333PX;
  border-radius: 10PX;
  padding: 13PX 16PX;
  min-height: 0;
  position: relative;
}

.amoe-form.isPC .phone-field .label,
.amoe-form.isPC .phone-field .req {
  font-size: 18PX;
  line-height: 1.4em;
  font-weight: 330;
}

.amoe-form.isPC .phone-field .label-row + .phone-input-row {
  margin-top: 2.666667PX;
}

.amoe-form.isPC .phone-input-row {
  font-size: 18PX;
  line-height: 1.4em;
}

.amoe-form.isPC .phone-input-row input {
  margin-left: 20PX;
  font-size: 18PX;
}

.amoe-form.isPC .state-text-column .value {
  font-size: 18PX;
  line-height: 1.4em;
}

.amoe-form.isPC .phone-field .error-msg {
  font-size: 14PX;
  padding: 4PX 16PX 0;
  line-height: 1.4em;
  height: 24PX;
}

.amoe-form.isPC .picker-trigger .chevron,
.amoe-form.isPC .phone-field .chevron {
  width: 12PX;
  height: 12PX;
  margin-left: 8PX;
  border-width: 2PX;
  top: -2PX;
}

.amoe-form.isPC .picker-trigger .label-row {
  padding: 0;
}

.amoe-form.isPC ::v-deep .sms-code-field .field-box {
  min-height: 0;
  height: 82PX;
  justify-content: center;
}

.amoe-form.isPC ::v-deep .agreement {
  padding: 0;
}

.country-option {
  display: flex;
  align-items: center;
  width: 100%;
  overflow: hidden;
  gap: 0.08rem;
}

.country-name {
  color: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
}

.country-code {
  color: #909499;
  flex-shrink: 0;
}

::v-deep .option-row.selected .country-code {
  color: inherit;
}
</style>
